import { CheckCircle2 } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  type Column,
  useToast,
} from '@/components/ui';
import {
  type WellbeingAlert,
  useWellbeingActions,
  useWellbeingAlerts,
} from '@/features/admin-v2/api';
import { isApiError } from '@/lib/api';
import { whenLabel } from '@/pages/calendar/time';
import styles from '@/pages/queue/queue.module.css';
import local from '@/pages/finance/finance.module.css';

/**
 * RL-1006: patients whose own Wellbeing screening in the app came out
 * "hard time". The app showed them the helplines and an urgent slot; a
 * practitioner follows up (call, offer a slot) and marks it done. The answers
 * are never shown here, only the total.
 */
export function WellbeingAlerts() {
  const toast = useToast();
  const alerts = useWellbeingAlerts(true);
  const { acknowledgeAlert } = useWellbeingActions();
  const acknowledge = (a: WellbeingAlert) =>
    acknowledgeAlert
      .mutateAsync(a.id)
      .then(() =>
        toast.show({
          tone: 'success',
          title: 'Marked as followed up',
          body: a.patient.fullName,
        }),
      )
      .catch((err: unknown) =>
        toast.show({
          tone: 'error',
          title: 'Not done',
          body: isApiError(err) ? err.message : undefined,
          error: err,
        }),
      );
  const columns: Column<WellbeingAlert>[] = [
    {
      key: 'when',
      header: 'Screened',
      render: (a) => whenLabel(a.submittedAt),
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (a) => (
        <>
          <p className={styles.name}>{a.patient.fullName}</p>
          <p className={styles.sub}>
            {a.patient.phone ? (
              <a href={`tel:${a.patient.phone.replace(/[^\d+]/g, '')}`}>
                {a.patient.phone}
              </a>
            ) : (
              'No phone on record'
            )}
            {a.branch ? ` · ${a.branch.name}` : ''}
          </p>
        </>
      ),
    },
    {
      key: 'result',
      header: 'Result',
      render: (a) => <Badge tone="danger">{`Hard time · ${a.total}`}</Badge>,
    },
    {
      key: 'session',
      header: 'Booked',
      render: (a) =>
        a.bookedSession ? (
          <Badge tone={a.bookedSession.urgent ? 'warning' : 'info'}>
            {`${a.bookedSession.urgent ? 'Urgent · ' : ''}${whenLabel(a.bookedSession.startsAt)}`}
          </Badge>
        ) : (
          'Not booked'
        ),
    },
    {
      key: 'ack',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (a) => (
        <Button
          size="sm"
          icon={<CheckCircle2 size={16} />}
          loading={
            acknowledgeAlert.isPending && acknowledgeAlert.variables === a.id
          }
          onClick={() => void acknowledge(a)}
          aria-label={`Mark ${a.patient.fullName} as followed up`}
        >
          Followed up
        </Button>
      ),
    },
  ];
  return (
    <Card
      title="Needs follow-up · app screening"
      flush
      actions={
        alerts.data?.length ? (
          <Badge tone="danger">{`${alerts.data.length} open`}</Badge>
        ) : undefined
      }
    >
      <DataTable
        caption="Patients whose app screening came out hard time"
        columns={columns}
        rows={alerts.data}
        rowKey={(a) => a.id}
        loading={alerts.isLoading}
        error={alerts.error}
        onRetry={() => void alerts.refetch()}
        empty={
          <EmptyState
            icon={<CheckCircle2 size={22} />}
            title="Nobody to follow up"
            body="Patients whose screening in the app comes out as a hard time appear here."
          />
        }
      />
      <p className={local.note}>
        These patients saw the helplines and an urgent slot today in the app.
        Call them, offer a session, then mark them as followed up. Only the
        total is shown, never the answers.
      </p>
    </Card>
  );
}
